/** Thin wrapper over the Learnica API. The session lives in an http-only cookie. */

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: any) {
    super(message);
  }
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: 'same-origin',
    headers: init.body ? { 'Content-Type': 'application/json' } : undefined,
    ...init,
  });
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) throw new ApiError(res.status, body?.error ?? res.statusText, body);
  return body as T;
}

export const api = {
  get: <T>(p: string) => call<T>(p),
  post: <T>(p: string, body?: unknown) => call<T>(p, { method: 'POST', body: body == null ? undefined : JSON.stringify(body) }),
  patch: <T>(p: string, body?: unknown) => call<T>(p, { method: 'PATCH', body: body == null ? undefined : JSON.stringify(body) }),
  del: <T>(p: string) => call<T>(p, { method: 'DELETE' }),
};

/* ───────────────────────────── Types ───────────────────────────── */

export type Me = {
  timezone: string;
  id: number; login: string; email: string | null;
  firstName: string; lastName: string | null;
  roles: string[]; activeRole: string;
  permissions: string[];
  avatarUrl?: string | null;
  mustResetPassword?: boolean;
  /** Present only while a member of staff is viewing the portal as this person. */
  impersonatedBy?: { id: number; name: string };
};

export type UnitType =
  | 'content' | 'video' | 'webpage' | 'audio' | 'document'
  | 'craft' | 'test' | 'survey' | 'assignment' | 'ilt' | 'section';

export type ProgressStatus = 'not_attempted' | 'incomplete' | 'completed' | 'failed' | 'pending_review';

export type CourseCard = {
  id: number; name: string; code: string | null;
  coverImagePath: string | null;
  completionPct: number; score: number | null;
  enrollmentStatus: string; unitCount: number;
  completedAt: string | null; expiresAt: string | null;
};

export type UnitSummary = {
  opensAt?: string | null;
  id: number; sectionId: number | null; position: number; type: UnitType;
  name: string; completionType: string;
  status: ProgressStatus; score: number | null; locked: boolean;
};

export type CourseDetail = {
  course: {
    id: number; name: string; code: string | null; descriptionHtml: string | null;
    coverImagePath: string | null; traversal: string;
  };
  enrollment: { id: number; status: string; completionPct: number; score: number | null } | null;
  sections: { id: number; name: string; position: number }[];
  units: UnitSummary[];
  prerequisites: {
    required: { id: number; name: string }[];
    missing: { id: number; name: string }[];
    met: boolean;
  } | null;
};

export type CraftBlock = Record<string, any>;

export type PublicationState = {
  hasDraft: boolean; publishedVersion: number; updatedAt: string | null; liveChanged: boolean;
  revisions: {
    id: number; version: number; kind: 'baseline' | 'saved' | 'published' | 'restored' | 'discarded';
    createdAt: string; createdById: number | null;
  }[];
};

export type CourseOverview = {
  name: string; code: string | null; descriptionHtml: string | null;
  introVideoUrl: string | null; coverImagePath: string | null;
};

export type CourseOverviewDocument = {
  version: number; overview: CourseOverview; preview: CourseOverview; publication: PublicationState;
};

export type CraftDocument = {
  version: number; blocks: CraftBlock[]; theme: Record<string, any> | null;
  preview: { blocks: CraftBlock[]; theme: Record<string, any> | null }; publication: PublicationState;
};

export type UnitDetail = {
  downloadDisabled?: boolean;
  id: number; courseId: number; type: UnitType; name: string;
  completionType: string; completionParams: Record<string, any> | null;
  contentHtml: string | null; secondaryHtml: string | null; url: string | null;
  settings: Record<string, any>;
  craft?: { theme: Record<string, any> | null; blocks: CraftBlock[]; options: Record<string, any> | null } | null;
  questionCount?: number;
  testInfo?: {
    passMark: number; attemptsLimit: number | null; repeatableWhen: string;
    shuffleQuestions: boolean; maxTimeSeconds: number | null;
    questionsPerAttempt?: number | null; questionPoolSize: number;
  };
  sessions?: ClassSession[];
  progress?: { status: ProgressStatus; score?: number | null };
};

export type PaperQuestion = {
  id: number; type: string; textHtml: string; weight: number; multipleAnswers: boolean;
  options?: { index: number | string; text: string }[];
  left?: string[]; right?: { index: number | string; text: string }[];
  gapCount?: number;
  scale?: string[];
  children?: { index: number; id: number; textHtml: string }[];
};

export type Attempt = {
  id: number; attemptNo: number; startedAt: string; deadlineAt: string | null;
  submittedAt: string | null; score: number | null; passed: boolean | null;
  passMark: number; questions: PaperQuestion[]; answers: Record<string, any>;
};

export type ReviewMode = 'after_submission' | 'after_pass' | 'after_final_attempt' | 'never';

export type AttemptStatus = {
  canStart: boolean; canResume: boolean; openAttemptId: number | null;
  reason: string | null; availableAt: string | null; retryAfterSeconds: number;
  attemptsRemaining: number | null; serverNow: string;
  attempts: {
    id: number; attemptNo: number; startedAt: string; submittedAt: string | null;
    score: number | null; passed: boolean | null; needsReview: boolean;
  }[];
};

export type SubmitResult = {
  attemptId: number; score: number | null; passMark: number;
  passed: boolean | null; needsReview: boolean; earned: number; possible: number;
  review?: { mode: ReviewMode; available: boolean; showFeedback: boolean; showCorrectAnswers: boolean; reason: string | null };
  results?: {
    questionId?: number; questionNumber?: number; textHtml?: string;
    correct: boolean | null; points: number; feedbackHtml?: string | null;
    correctAnswers?: any; correctAnswerText?: string[]; comment?: string | null;
  }[];
  course?: { completionPct: number; score: number | null; status: string; completed: boolean };
};

/* ───────────────────────── Admin ───────────────────────── */

export type AdminUserRow = {
  id: number; login: string; email: string | null; phone?: string | null;
  firstName: string; lastName: string;
  userTypeId: number; userTypeName: string;
  status: 'active' | 'inactive';
  /** Whether they can sign in at all. Never the hash itself. */
  hasPassword: boolean;
  mustResetPassword: boolean;
  lastLoginAt: string | null;
  courseCount: number;
};

export type UserTypeRow = {
  id: number; name: string; defaultRole: string;
  isDefault: boolean; assignable: boolean; permissionCount: number;
};

export type AdminUserDetail = {
  user: AdminUserRow & {
    bio: string | null; timezone: string;
  };
  enrolments: {
    courseId: number; courseName: string; courseStatus: string;
    role: string; status: string; completionPct: number; score: number | null;
    enrolledAt: string; completedAt: string | null;
  }[];
  groups: { id: number; name: string; isSupervisor: boolean }[];
};

export type AdminCourseRow = {
  id: number; name: string; code: string | null;
  status: 'active' | 'inactive';
  coverImagePath: string | null; traversal: string; updatedAt: string;
  unitCount: number; enrolledCount: number; completedCount: number;
};

export type CohortRow = {
  id: number; name: string; description: string | null;
  groupKey: string | null; autoEnroll: boolean;
  memberCount: number; courseCount: number;
};

export type Dashboard = {
  counts: {
    people: number; activePeople: number; awaitingFirstPassword: number;
    courses: number; activeCourses: number; cohorts: number;
    enrolments: number; completed: number; inProgress: number; notStarted: number;
    awaitingMarking: number;
  };
  busiest: { id: number; name: string; status: string; enrolled: number; completed: number }[];
};

export type CoursePeople = {
  course: { id: number; name: string };
  total: number;
  people: {
    userId: number; login: string; firstName: string; lastName: string;
    email: string | null; accountStatus: string;
    role: string; status: string; completionPct: number; score: number | null;
    enrolledAt: string; completedAt: string | null; lastAccessedAt: string | null;
  }[];
};

/* ───────────────────────── Builder ───────────────────────── */

export type BuilderUnitRow = {
  id: number; position: number; type: UnitType; name: string;
  isActive: boolean; completionType: string;
  url: string | null; mediaFileId: number | null;
  hasContent: boolean; questionCount: number; learnersStarted: number;
};

export type BuilderCourse = {
  course: {
    id: number; name: string; code: string | null; descriptionHtml: string | null;
    status: 'active' | 'inactive'; traversal: string; categoryId: number | null;
    timeLimitDays: number | null;
    certificateTemplateId: number | null;
    certificateValidDays: number | null;
    completionRule: CompletionRule;
    scoreRule: ScoreRule;
    capacity: number | null;
    isHiddenFromCatalog: boolean;
    isContentLocked: boolean;
    enrollmentApproval: boolean;
    retainAccessAfterCompletion: boolean;
    coverImagePath: string | null;
    introVideoUrl: string | null;
  };
  units: BuilderUnitRow[];
  enrolledCount: number;
  prerequisiteIds: number[];
  otherCourses: { id: number; name: string }[];
};

export type CompletionRule =
  | { type: 'all_units' }
  | { type: 'percentage'; value: number }
  | { type: 'units'; unitIds: number[] }
  | { type: 'test'; unitId: number };

export type ScoreRule =
  | { type: 'tests_only' }
  | { type: 'tests_and_assignments' }
  | { type: 'weighted'; weights: Record<string, number> };

export type BuilderQuestion = {
  children?: any[] | null;
  id: number; position: number; type: string; textHtml: string;
  feedbackHtml: string | null; weight: number; multipleAnswers: boolean;
  answers: Record<string, any>; correctAnswers: Record<string, any> | null;
  pointRules: { type: string; text: string; points: number }[] | null;
};

export type BuilderUnit = {
  unit: {
    id: number; courseId: number; type: UnitType; name: string; isActive: boolean;
    contentHtml: string | null; secondaryHtml: string | null; url: string | null;
    mediaFileId: number | null; completionType: string; previewUrl?: string; availability?: Record<string, any> | null;
    completionParams: Record<string, any> | null;
    settings: Record<string, any>;
    questions?: BuilderQuestion[];
  };
  learnersStarted: number;
  submittedAttempts: number;
};

export type UploadedFile = {
  id: number; storagePath: string; originalName: string | null;
  mimeType: string | null; sizeBytes: number | null;
};

/** POST a file with progress. Returns the stored record. */
export function uploadFile(file: File, onProgress?: (pct: number) => void, courseId?: number): Promise<UploadedFile> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    form.append('file', file);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/admin/uploads' + (courseId ? '?courseId=' + courseId : ''));
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => { if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => {
      let body: any = null;
      try { body = JSON.parse(xhr.responseText); } catch { /* not json */ }
      if (xhr.status >= 200 && xhr.status < 300) resolve(body.file);
      else reject(new ApiError(xhr.status, body?.error ?? 'That upload failed.', body));
    };
    xhr.onerror = () => reject(new ApiError(0, 'The connection dropped during the upload.'));
    xhr.send(form);
  });
}

/* ─────────────────────── Marking (instructor) ─────────────────────── */

export type MarkingTestRow = {
  attemptId: number; submittedAt: string; attemptNo: number;
  unitId: number; unitName: string;
  courseId: number; courseName: string;
  userId: number; firstName: string; lastName: string | null;
};

export type MarkingAssignmentRow = {
  submissionId: number; submittedAt: string; hasFile: boolean;
  unitId: number; unitName: string;
  courseId: number; courseName: string;
  userId: number; firstName: string; lastName: string | null;
};

export type MarkingQueue = {
  tests: MarkingTestRow[];
  assignments: MarkingAssignmentRow[];
  counts: { tests: number; assignments: number };
  scope: 'all' | 'mine' | 'none';
};

export type MarkQuestion = {
  id: number; type: string; textHtml: string; weight: number;
  needsReview: boolean; points: number; correct: boolean | null; comment: string | null;
  given: string[]; expected: string[];
};

export type MarkTestPaper = {
  attempt: { id: number; attemptNo: number; submittedAt: string; score: number | null; passed: boolean | null; needsReview: boolean; passMark: number };
  unit: { id: number; name: string; type: string };
  course: { id: number; name: string };
  learner: { id: number; firstName: string; lastName: string | null; login: string };
  questions: MarkQuestion[];
  awaiting: number;
};

export type MarkAssignmentView = {
  submission: { id: number; text: string | null; submittedAt: string; grade: number | null; passed: boolean | null; feedback: string | null; gradedAt: string | null };
  file: { id: number; name: string | null; mimeType: string | null; sizeBytes: number | null } | null;
  unit: { id: number; name: string; promptHtml: string | null };
  course: { id: number; name: string };
  learner: { id: number; firstName: string; lastName: string | null; login: string };
  history: { id: number; submittedAt: string; grade: number | null; passed: boolean | null; feedback: string | null }[];
};

export type SessionRow = {
  id: number; name: string; startsAt: string; durationMinutes: number;
  location: string | null; conferenceUrl: string | null; capacity: number | null;
  unitId: number; unitName: string; courseId: number; courseName: string;
  registered: number; unmarked: number;
};

export type SessionView = {
  session: SessionRow;
  unit: { id: number; name: string };
  course: { id: number; name: string };
  people: {
    userId: number; firstName: string; lastName: string | null; login: string;
    attended: boolean | null; grade: number | null; comment: string | null;
  }[];
};

/* ─────────────────────── Assignments (learner) ─────────────────────── */

export type MySubmission = {
  id: number; text: string | null; mediaFileId: number | null; submittedAt: string;
  grade: number | null; passed: boolean | null; feedback: string | null; gradedAt: string | null;
};

export type AssignmentView = {
  unit: { id: number; name: string; promptHtml: string | null };
  unlocked: boolean;
  submissions: MySubmission[];
  waiting: boolean;
  canSubmit: boolean;
  accepts: string;
};

/** Hand in an assignment: text, a file, or both. Reports upload progress. */
export function submitAssignment(
  courseId: number, unitId: number,
  payload: { text?: string; file?: File | null },
  onProgress?: (pct: number) => void,
): Promise<{ submission: { id: number; submittedAt: string }; status: string }> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    if (payload.text) form.append('text', payload.text);
    if (payload.file) form.append('file', payload.file);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/courses/${courseId}/units/${unitId}/assignment`);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => { if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => {
      let body: any = null;
      try { body = JSON.parse(xhr.responseText); } catch { /* not json */ }
      if (xhr.status >= 200 && xhr.status < 300) resolve(body);
      else reject(new ApiError(xhr.status, body?.error ?? 'That hand-in failed.', body));
    };
    xhr.onerror = () => reject(new ApiError(0, 'The connection dropped while handing in.'));
    xhr.send(form);
  });
}

/* ─────────────────────── Certificates ─────────────────────── */

export type Standing = 'valid' | 'expired' | 'revoked';

export type MyCertificate = {
  publicId: string; courseId: number; courseName: string;
  issuedAt: string; expiresAt: string | null; score: number | null;
  standing: Standing;
  /** Whether this one can be downloaded as a file, or only printed. */
  asFile: boolean;
};

export type AdminCertificate = {
  id: number; publicId: string;
  issuedAt: string; expiresAt: string | null; revokedAt: string | null; score: number | null;
  userId: number; firstName: string; lastName: string | null; login: string;
  courseId: number; courseName: string;
  standing: Standing;
};

export type CertificateTemplateRow = {
  id: number; name: string; hasBackground: boolean; courses: number;
  tags: string[]; unsupportedTags: string[]; laidOut: boolean;
};

/** One line of text on a certificate, as a share of the page. */
export type CertificateBlock = {
  text: string;
  /** How far down the page, 0–100. */
  top: number;
  /** Points, the same units the PDF is drawn in. */
  size: number;
  bold?: boolean;
  italic?: boolean;
  align?: 'left' | 'center' | 'right';
  color?: string;
  /** Space either side, as a percentage of the page width. */
  margin?: number;
  /** Cormorant Garamond ('serif', the default) or Plus Jakarta Sans ('sans'). */
  font?: 'serif' | 'sans';
  /** Shrink the type until the line fits on one line — for names and courses. */
  fit?: boolean;
};
export type CertificateLayout = { blocks: CertificateBlock[] };

export type CertificateTemplateDetail = {
  id: number; name: string; bodyHtml: string;
  layout: CertificateLayout | null;
  hasBackground: boolean; backgroundUrl: string | null; courses: number;
};

/** The page the PDF is drawn on, in points — the preview matches it exactly. */
export const CERTIFICATE_PAGE = { width: 841.89, height: 595.28 };

export const templatePdfUrl = (id: number) => `/api/admin/certificates/templates/${id}/pdf`;
export const certificatePdfUrl = (publicId: string) => `/api/certificates/${publicId}/pdf`;

export type CertificateCourseRow = {
  id: number; name: string; status: string;
  templateId: number | null; templateName: string | null;
  validDays: number | null; issued: number; completed: number;
};

export type VerifyResult = {
  holder: string; courseName: string; courseCode: string | null;
  issuedAt: string; expiresAt: string | null; standing: Standing; issuedBy: string;
};

/** Where the printable certificate lives. Opened in a new tab, printed from there. */
export const certificateUrl = (publicId: string) => `/api/certificates/${publicId}/view`;

/* ─────────────────────── Progress & timeline ─────────────────────── */

export type Blocker =
  | { kind: 'prerequisite'; text: string; courses: { id: number; name: string }[] }
  | { kind: 'awaiting_marking'; text: string; since: string; unitName: string }
  | { kind: 'no_attempts_left'; text: string; unitName: string }
  | { kind: 'class_ahead'; text: string; at: string; unitName: string }
  | { kind: 'opens_later'; text: string; at: string; unitName: string };

export type CourseProgress = {
  courseId: number; courseName: string; status: string;
  completionPct: number; score: number | null;
  enrolledAt: string | null; completedAt: string | null;
  expiresAt: string | null; lastAccessedAt: string | null;
  unitsDone: number; unitsTotal: number;
  nextUnit: { id: number; name: string; type: string } | null;
  blocker: Blocker | null;
  overdue: boolean;
};

export type TimelineEvent = {
  at: string;
  kind: 'enrolled' | 'unit' | 'test' | 'assignment' | 'marked' | 'class' | 'certificate' | 'completed';
  title: string; detail: string | null;
  courseId: number | null; courseName: string | null;
};

export type ProgressReport = {
  person: { id: number; firstName: string; lastName: string | null; login: string };
  summary: {
    courses: number; completed: number; inProgress: number; notStarted: number;
    blocked: number; overdue: number; certificates: number; lastActiveAt: string | null;
  };
  courses: CourseProgress[];
  timeline: TimelineEvent[];
};

/* ─────────────────────── Files repository ─────────────────────── */

export type SharedFile = {
  id: number;
  mediaFileId: number;
  name: string;
  description: string | null;
  mimeType: string | null;
  sizeBytes: number | null;
  /** null for a portal-wide file; otherwise the cohort it came with. */
  cohortId: number | null;
  cohortName: string | null;
  sharedAt: string;
};

export type FileReach = {
  portalWide: boolean;
  cohorts: { shareId: number; cohortId: number; cohortName: string | null }[];
  alsoUsedBy: { units: number; courseFiles: number };
};

export const fileUrl = (mediaFileId: number) => `/api/media/${mediaFileId}`;
export const fileDownloadUrl = (mediaFileId: number) => `/api/media/${mediaFileId}?download=1`;

/** Human file size. Bytes on a screen are nobody's unit. */
export function fileSize(bytes: number | null): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['kB', 'MB', 'GB'];
  let n = bytes / 1024, i = 0;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i += 1; }
  return `${n < 10 ? n.toFixed(1) : Math.round(n)} ${units[i]}`;
}

/* ─────────────────────── Reports ─────────────────────── */

export type CourseReportRow = {
  id: number; name: string; code: string | null; status: string;
  timeLimitDays: number | null;
  enrolled: number; completed: number; inProgress: number; notStarted: number; failed: number;
  completionRate: number | null; avgScore: number | null; avgDays: number | null;
  overdue: number; awaitingMarking: number; certificates: number;
};

export type PersonReportRow = {
  id: number; firstName: string; lastName: string | null; login: string;
  email: string | null; phone: string | null; status: string; lastLoginAt: string | null;
  assigned: number; completed: number; inProgress: number; notStarted: number;
  overdue: number; avgScore: number | null; lastActivity: string | null; certificates: number;
};

export type MatrixCell = {
  status: string; pct: number; score: number | null; overdue: boolean; expiresAt: string | null;
};

export type MatrixReport = {
  courses: { id: number; name: string; code: string | null }[];
  people: {
    userId: number; firstName: string; lastName: string | null; login: string;
    cells: Record<string, MatrixCell>;
  }[];
  note?: string;
};

export type ExpiringRow = {
  id: number; publicId: string; issuedAt: string; expiresAt: string | null;
  firstName: string; lastName: string | null; login: string; email: string | null;
  courseId: number; courseName: string; standing: Standing; daysLeft: number | null;
};

export type CohortOption = { id: number; name: string; members: number; courses: number };

export type GradeCell = { score: number | null; attempts: number; result: 'passed' | 'failed' | 'waiting' | 'started' };

export type Gradebook = {
  course: { id: number; name: string; status: string };
  units: { id: number; name: string; type: 'test' | 'assignment'; isActive: boolean; passMark: number | null }[];
  people: {
    userId: number; firstName: string; lastName: string | null; login: string;
    status: string; completionPct: number; score: number | null;
    cells: Record<string, GradeCell>;
  }[];
  summary: Record<string, { taken: number; passed: number; waiting: number; average: number | null }>;
};


/* ─────────────────────── Live classes ─────────────────────── */

export type ClassSession = {
  id: number; name: string; description: string | null;
  startsAt: string; durationMinutes: number;
  capacity: number | null; location: string | null; conferenceUrl: string | null;
  instructorId: number | null; instructorName: string | null;
  taken: number; placesLeft: number | null; full: boolean; started: boolean;
  mine: boolean; attended: boolean | null; canBook: boolean; canCancel: boolean;
};

export type MyClass = {
  sessionId: number; name: string; startsAt: string; durationMinutes: number;
  location: string | null; conferenceUrl: string | null; attended: boolean | null;
  unitId: number; unitName: string; courseId: number; courseName: string;
};

export type SessionCandidate = { id: number; firstName: string; lastName: string | null; login: string };

/** The class as a calendar file, for the trainee who holds a place on it. */
export const classCalendarUrl = (courseId: number, unitId: number, sessionId: number) =>
  `/api/courses/${courseId}/units/${unitId}/sessions/${sessionId}/calendar.ics`;

/* ─────────────────────── Notifications ─────────────────────── */

export type NotificationTemplateRow = {
  id: number; name: string; event: string; eventLabel: string; known: boolean;
  recipient: string; subject: string | null; active: boolean;
  whatsappTemplate: string | null;
  sent: Record<string, number>;
  unsupportedTags: string[];
};

export type NotificationsView = {
  templates: NotificationTemplateRow[];
  events: { event: string; label: string }[];
  tags: Record<string, string>;
  delivery: {
    driver: string; channel: string; live: boolean; from: string;
    needsWhatsappTemplate?: boolean;
  };
};

export type NotificationTemplate = {
  id: number; name: string; event: string; recipient: string;
  subject: string | null; body: string; active: boolean;
  params: { whatsapp?: { contentSid: string; variables: string[] } | null } | null;
};

export type OutboxRow = {
  id: number; channel: string; address: string | null;
  subject: string | null; body: string; status: string;
  attempts: number; error: string | null;
  createdAt: string; sentAt: string | null;
  firstName: string | null; lastName: string | null; login: string | null;
  templateName: string | null;
};

/* ─────────────────── Attendance from a meeting report ─────────────────── */

export type ImportPreview = {
  dryRun: true;
  linesRead: number;
  present: { userId: number; name: string; minutes: number | null; by: string }[];
  tooShort: { userId: number; name: string; minutes: number | null }[];
  unmatched: { name: string; email: string | null; minutes: number | null; reason: string }[];
  absent: { userId: number; name: string }[];
  hasDurations: boolean;
};

export type ImportResult = {
  dryRun: false; marked: number; present: number; absent: number;
  unmatched: { name: string; reason: string }[];
};
